// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * NewStep12FuneralCosts - Uitvaartverzekering & Kosten
 * Stap 16/19: direct na Uitvaartwensen
 * moduleKey: 'funeralCosts'
 */
import React from 'react'
import { useState, useEffect, useCallback } from 'react'
import { AlertTriangle, Euro, User } from 'lucide-react'
import { Card }       from '@/components/ui/Card'
import { Input }      from '@/components/ui/Input'
import { Textarea }   from '@/components/ui/Textarea'
import { InfoBlock }  from '@/components/ui/InfoBlock'
import { InfoButton } from '@/components/ui/InfoButton'
import { Toggle }     from '@/components/ui/Toggle'
import { useAuthStore } from '@/store'
import { useAutosave }  from '@/hooks/useAutosave'
import { wizardLoad, wizardSave, loadPartner, loadChildren, loadInsurance, NL_INSURERS, type PartnerData, type ChildrenData } from '@/services/wizard'
import { ClosingGuidance } from '@/components/process/ClosingGuidance'
import { loadTrustedPersons, type TrustedPersonConfig } from '@/services/trustedPerson'
import type { SaveStatus } from '@/hooks/useAutosave'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FuneralCostsData {
  hasInsurance:        string   // 'yes' | 'no' | 'unknown'
  insurer:             string
  insurerOther:        string
  policyNumber:        string
  insuredAmount:       string
  docsLocation:        string
  insuranceNotes:      string
  paymentMethods:      string[] // meerkeuze
  paymentNotes:        string
  estimatedBudget:     string
  financialContact:    string
  financialContactCustom:      string  // legacy vrije tekst (backward compat)
  // V39: gestructureerde contactvelden
  financialContactName:         string
  financialContactRole:         string
  financialContactPhone:        string
  financialContactEmail:        string
  financialContactInstructions: string
  // checklist
  checkPolicyKnown:    boolean
  checkPolicyNrKnown:  boolean
  checkBankKnown:      boolean
  checkVPSet:          boolean
  checkDocsKnown:      boolean
}

const EMPTY: FuneralCostsData = {
  hasInsurance: '', insurer: '', insurerOther: '', policyNumber: '',
  insuredAmount: '', docsLocation: '', insuranceNotes: '',
  paymentMethods: [], paymentNotes: '', estimatedBudget: '',
  financialContact: '', financialContactCustom: '',
  financialContactName: '', financialContactRole: '', financialContactPhone: '',
  financialContactEmail: '', financialContactInstructions: '',
  checkPolicyKnown: false, checkPolicyNrKnown: false, checkBankKnown: false,
  checkVPSet: false, checkDocsKnown: false,
}

const INSURERS = NL_INSURERS

const PAYMENT_OPTIONS = [
  { value: 'insurance',    label: 'Uitvaartverzekering' },
  { value: 'savings',      label: 'Spaargeld' },
  { value: 'investments',  label: 'Beleggingen' },
  { value: 'assets',       label: 'Verkoop bezittingen' },
  { value: 'estate',       label: 'Nalatenschap' },
  { value: 'combination',  label: 'Combinatie van bovenstaande' },
  { value: 'none',         label: 'Geen voorkeur' },
]

const MODULE_KEY = 'funeralCosts'

// ── Component ─────────────────────────────────────────────────────────────────

export function NewStep12FuneralCosts({ onSaveStatus, saveRef }: { onSaveStatus: (s: SaveStatus) => void; saveRef?: React.MutableRefObject<(() => Promise<void>) | null> }) {
  const { userId }       = useAuthStore()
  const [data, setData]  = useState<FuneralCostsData>(EMPTY)
  const [ready, setReady] = useState(false)

  // Context voor contactpicker
  const [ctxPartner,  setCtxPartner]  = useState<PartnerData | null>(null)
  const [ctxChildren, setCtxChildren] = useState<ChildrenData | null>(null)
  const [ctxVP,       setCtxVP]       = useState<TrustedPersonConfig[]>([])

  useEffect(() => {
    if (!userId) return
    onSaveStatus('idle')
    Promise.all([
      wizardLoad<FuneralCostsData>(userId, MODULE_KEY),
      loadPartner(userId),
      loadChildren(userId),
      loadTrustedPersons(userId),
      loadInsurance(userId),
    ]).then(([saved, partner, children, vps, insurance]) => {
      const base: FuneralCostsData = { ...EMPTY, ...(saved ?? {}) }

      // Prefill hasInsurance uit verzekeringen-stap als het veld nog leeg is
      if (!base.hasInsurance) {
        const heeftUitvaart = insurance?.insurances?.some(
          ins => ins.type === 'Uitvaartverzekering'
        )
        if (heeftUitvaart) {
          base.hasInsurance = 'yes'
          // Prefill verzekeraar en polisnummer als die nog leeg zijn
          const uitvaartIns = insurance!.insurances.find(ins => ins.type === 'Uitvaartverzekering')!
          if (!base.insurer    && uitvaartIns.insurer)      base.insurer      = uitvaartIns.insurer
          if (!base.policyNumber && uitvaartIns.policyNumber) base.policyNumber = uitvaartIns.policyNumber
        }
      }

      setData(base)
      if (partner) setCtxPartner(partner)
      if (children) setCtxChildren(children)
      if (vps?.length) setCtxVP(vps)
      setReady(true)
    })
  }, [userId, onSaveStatus])

  const save = useCallback(async (d: FuneralCostsData) => {
    if (!userId) return
    const filled = [d.hasInsurance, d.paymentMethods.length > 0].filter(Boolean).length
    await wizardSave(userId, MODULE_KEY, d, filled)
  }, [userId])

  const { status, saveNow } = useAutosave({ data, onSave: save, enabled: ready })
  useEffect(() => {
    if (saveRef) saveRef.current = saveNow
    return () => { if (saveRef) saveRef.current = null }
  }, [saveRef, saveNow])
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  function upd<K extends keyof FuneralCostsData>(k: K, v: FuneralCostsData[K]) {
    setData(p => ({ ...p, [k]: v }))
  }

  function togglePayment(val: string) {
    setData(p => ({
      ...p,
      paymentMethods: p.paymentMethods.includes(val)
        ? p.paymentMethods.filter(x => x !== val)
        : [...p.paymentMethods, val],
    }))
  }

  // Bouw contactsuggesties - V39: dedupliceer op voornaam (combineer rollen)
  type ContactEntry = { value: string; label: string; firstName: string; roles: string[] }
  const rawContacts: ContactEntry[] = []

  if (ctxPartner?.hasPartner === 'yes' && ctxPartner.name) {
    rawContacts.push({
      value: 'partner',
      label: '',
      firstName: ctxPartner.name.toLowerCase(),
      roles: [`Partner - ${ctxPartner.name}`],
    })
  }
  ctxVP.forEach(vp => {
    const existing = rawContacts.find(c => c.firstName === vp.firstName.toLowerCase())
    if (existing) {
      existing.roles.push('Vertrouwenspersoon')
      existing.value = `vp_${vp.id}` // VP heeft prioriteit als opgeslagen waarde
    } else {
      rawContacts.push({ value: `vp_${vp.id}`, label: '', firstName: vp.firstName.toLowerCase(), roles: [`Vertrouwenspersoon - ${vp.firstName}`] })
    }
  })
  ctxChildren?.children?.filter(c => c.name).forEach((c, i) => {
    const existing = rawContacts.find(rc => rc.firstName === c.name.toLowerCase())
    if (existing) {
      existing.roles.push('Kind')
    } else {
      rawContacts.push({ value: `child_${i}`, label: '', firstName: c.name.toLowerCase(), roles: [`Kind - ${c.name}`] })
    }
  })

  // Genereer gecombineerd label
  const contacts = rawContacts.map(rc => {
    if (rc.roles.length === 1) return { value: rc.value, label: rc.roles[0] }
    // Meerdere rollen: "{naam} - {rol1} & {rol2}"
    const name = rc.firstName.charAt(0).toUpperCase() + rc.firstName.slice(1)
    const roleStr = rc.roles.map(r => r.split(' - ')[0]).join(' & ')
    return { value: rc.value, label: `${name} - ${roleStr}` }
  })
  contacts.push({ value: 'other', label: 'Handmatig invullen' })

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-semibold text-ink mb-1">Uitvaartverzekering &amp; Kosten</h2>
        <p className="text-sm text-ink-3 leading-relaxed">
          Geef uw nabestaanden inzicht hoe de uitvaart betaald kan worden
          en waar zij rekening mee moeten houden.
        </p>
      </div>

      {/* ── 1. HEEFT U EEN UITVAARTVERZEKERING? ─────────────────────────── */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-ink">Heeft u een uitvaartverzekering?</h3>
          <InfoButton title="Uitvaartverzekering"
            content={
              <p>Een uitvaartverzekering kan (een deel van) de kosten van een uitvaart vergoeden.
              Controleer of de polis nog actief is en waar de documenten zich bevinden.</p>
            } />
        </div>
        <div className="flex flex-col gap-2">
          {[
            { value: 'yes',     label: 'Ja' },
            { value: 'no',      label: 'Nee' },
            { value: 'unknown', label: 'Weet ik niet' },
          ].map(opt => (
            <button key={opt.value} type="button"
              onClick={() => upd('hasInsurance', opt.value)}
              className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                data.hasInsurance === opt.value
                  ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'
              }`}>
              <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                data.hasInsurance === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
              }`} />
              <span className="text-sm font-medium text-ink">{opt.label}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* ── 2. INDIEN JA: verzekeringsdetails ──────────────────────────────── */}
      {data.hasInsurance === 'yes' && (
        <Card className="animate-slide-down">
          <h3 className="text-sm font-semibold text-ink mb-3">Gegevens uitvaartverzekering</h3>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-ink-2">Verzekeraar</label>
              <select value={data.insurer}
                onChange={e => upd('insurer', e.target.value)}
                className="rounded-xl border-2 border-sand-300 bg-white px-4 py-3 text-sm text-ink focus:border-sage-400 focus:outline-none transition-colors">
                <option value="">Selecteer verzekeraar...</option>
                {INSURERS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {data.insurer === 'Anders' && (
              <Input label="Naam verzekeraar" value={data.insurerOther}
                onChange={e => upd('insurerOther', e.target.value)}
                placeholder="Naam van de verzekeraar" />
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Polisnummer" value={data.policyNumber}
                onChange={e => upd('policyNumber', e.target.value)}
                placeholder="Bijv: POL-123456" />
              <Input label="Verzekerd bedrag (optioneel)" value={data.insuredAmount}
                onChange={e => upd('insuredAmount', e.target.value)}
                placeholder="Bijv: € 8.000" />
            </div>
            <Input label="Locatie van de polis / documenten" value={data.docsLocation}
              onChange={e => upd('docsLocation', e.target.value)}
              placeholder="Bijv: Rode map in bureaulade, bij notaris..." />
            <Textarea label="Opmerkingen (optioneel)" value={data.insuranceNotes}
              onChange={e => upd('insuranceNotes', e.target.value)}
              placeholder="Bijv: natura-polis, loopt tot 2040, contact: 088-..." maxLength={300} />
          </div>
        </Card>
      )}

      {/* ── 3. INDIEN NEE: informatiekaart ─────────────────────────────────── */}
      {data.hasInsurance === 'no' && (
        <div className="animate-slide-down rounded-2xl bg-sand border border-sand-300 px-5 py-4">
          <p className="text-sm text-ink-3 leading-relaxed">
            <strong className="text-ink">Geen uitvaartverzekering?</strong>{' '}
            Veel mensen kiezen ervoor de uitvaart later te betalen uit spaargeld, beleggingen
            of de nalatenschap. Dit is niet ongebruikelijk.
          </p>
        </div>
      )}

      {/* ── 4. HOE MOET DE UITVAART WORDEN BETAALD? ────────────────────────── */}
      <Card>
        <h3 className="text-sm font-semibold text-ink mb-3">Hoe moet de uitvaart worden betaald?</h3>
        <p className="text-xs text-ink-4 mb-3">Meerdere keuzes mogelijk.</p>
        <div className="flex flex-col gap-2">
          {PAYMENT_OPTIONS.map(opt => (
            <button key={opt.value} type="button"
              onClick={() => togglePayment(opt.value)}
              className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                data.paymentMethods.includes(opt.value)
                  ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'
              }`}>
              <div className={`h-4 w-4 rounded-md border-2 flex-shrink-0 flex items-center justify-center ${
                data.paymentMethods.includes(opt.value)
                  ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
              }`}>
                {data.paymentMethods.includes(opt.value) && (
                  <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 10 10">
                    <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className="text-sm text-ink">{opt.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-3">
          <Textarea label="Toelichting voor nabestaanden (optioneel)" value={data.paymentNotes}
            onChange={e => upd('paymentNotes', e.target.value)}
            placeholder="Bijv: gebruik eerst de verzekering, daarna spaargeld op ING-rekening..." maxLength={400} />
        </div>
      </Card>

      {/* ── 5. WAARSCHUWING BANKREKENINGEN ──────────────────────────────────── */}
      <div className="rounded-2xl bg-status-warnBg border border-status-warnBorder px-5 py-4 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-status-warnText flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-status-warnText mb-2">Let op - bankrekeningen na overlijden</p>
          <p className="text-xs text-status-warnText leading-relaxed mb-2">
            Na een overlijden kunnen bankrekeningen tijdelijk beperkt toegankelijk zijn
            totdat de bank de nalatenschap heeft verwerkt.
          </p>
          <p className="text-xs font-semibold text-status-warnText mb-1">Zorg daarom dat uw nabestaanden weten:</p>
          <ul className="text-xs text-status-warnText space-y-0.5 list-disc list-inside">
            <li>welke banken u gebruikt</li>
            <li>waar documenten liggen</li>
            <li>wie uw vertrouwenspersoon is</li>
            <li>hoe de uitvaart betaald kan worden</li>
          </ul>
        </div>
      </div>

      {/* ── 6. PRAKTISCHE UITLEG ────────────────────────────────────────────── */}
      <InfoBlock title="Uitvaartfactuur rechtstreeks betalen" variant="blue" defaultOpen={false}>
        <p>
          Goed om te weten: veel banken kunnen een uitvaartfactuur rechtstreeks betalen
          vanuit de rekening van de overledene, vaak al vóór de nalatenschap is afgewikkeld.
          Uw nabestaanden kunnen dat navragen bij de betreffende bank, zodat zij niets
          hoeven voor te schieten.
        </p>
      </InfoBlock>

      {/* ── 7. BESCHIKBAAR BEDRAG ───────────────────────────────────────────── */}
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Euro className="h-4 w-4 text-sage-600" />
          <h3 className="text-sm font-semibold text-ink">Geschat beschikbaar bedrag</h3>
          <InfoButton title="Bedrag als indicatie"
            content={<p>Dit bedrag is uitsluitend bedoeld als indicatie voor uw nabestaanden. Het heeft geen juridische of financiële waarde.</p>} />
        </div>
        <Input label="Bedrag (optioneel)" value={data.estimatedBudget}
          onChange={e => upd('estimatedBudget', e.target.value)}
          placeholder="Bijv: € 7.500"
          hint="Alleen als indicatie - geen verplichting" />
      </Card>

      {/* ── 8. CONTACTPERSOON FINANCIËN ─────────────────────────────────────── */}
      <Card>
        <div className="flex items-center gap-2 mb-2">
          <User className="h-4 w-4 text-sage-600" />
          <h3 className="text-sm font-semibold text-ink">Contactpersoon voor financiële zaken</h3>
        </div>
        <p className="text-xs text-ink-4 mb-3">
          Wie kan nabestaanden helpen met financiële en administratieve zaken na uw overlijden?
          Denk aan bankzaken, belastingaangifte, verzekeringen of zakelijke administratie.
        </p>
        {contacts.length > 0 && (
          <div className="flex flex-col gap-2 mb-3">
            {contacts.map(c => (
              <button key={c.value} type="button"
                onClick={() => upd('financialContact', c.value)}
                className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                  data.financialContact === c.value
                    ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'
                }`}>
                <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                  data.financialContact === c.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                }`} />
                <span className="text-sm text-ink">{c.label}</span>
              </button>
            ))}
          </div>
        )}
        {(data.financialContact === 'other' || contacts.length === 0) && (
          <div className="flex flex-col gap-3 border-t border-sand-200 pt-3 animate-slide-down">
            <p className="text-xs text-ink-4 leading-relaxed">
              Deze persoon of organisatie kan uw nabestaanden helpen met financiële zaken
              zoals bankzaken, belastingen, verzekeringen of zakelijke administratie.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Naam organisatie of contactpersoon"
                value={data.financialContactName}
                onChange={e => upd('financialContactName', e.target.value)}
                placeholder="Bijv: Administratiekantoor Jansen, mevr. De Vries" />
              <Input label="Relatie / functie"
                value={data.financialContactRole}
                onChange={e => upd('financialContactRole', e.target.value)}
                placeholder="Bijv: boekhouder, notaris, financieel adviseur" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Telefoonnummer" type="tel"
                value={data.financialContactPhone}
                onChange={e => upd('financialContactPhone', e.target.value)}
                placeholder="020-1234567" />
              <Input label="E-mailadres" type="email"
                value={data.financialContactEmail}
                onChange={e => upd('financialContactEmail', e.target.value)}
                placeholder="naam@kantoor.nl" />
            </div>
            <Textarea label="Aanvullende instructies voor nabestaanden (optioneel)"
              value={data.financialContactInstructions}
              onChange={e => upd('financialContactInstructions', e.target.value)}
              placeholder="Bijv: vraag naar de dossiermap met ref. JdV-2024, vermeld dat het gaat om de nalatenschap van..."
              maxLength={400} />
            {/* Backward compat: toon legacy vrije tekst als die ingevuld was */}
            {data.financialContactCustom && !data.financialContactName && (
              <div className="rounded-xl bg-sand-100 border border-sand-300 px-4 py-3">
                <p className="text-xs font-semibold text-ink-4 mb-1">Eerder ingevoerde tekst</p>
                <p className="text-xs text-ink">{data.financialContactCustom}</p>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ── 9. DOCUMENTEN CHECKLIST ─────────────────────────────────────────── */}
      <Card>
        <h3 className="text-sm font-semibold text-ink mb-3">Checklist documenten</h3>
        <div className="flex flex-col gap-3">
          {[
            { key: 'checkPolicyKnown',  label: 'Polis uitvaartverzekering bekend' },
            { key: 'checkPolicyNrKnown',label: 'Polisnummer bekend' },
            { key: 'checkBankKnown',    label: 'Bankrekeningen vastgelegd' },
            { key: 'checkVPSet',        label: 'Vertrouwenspersoon ingesteld' },
            { key: 'checkDocsKnown',    label: 'Documentenlocatie bekend' },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between">
              <span className={`text-sm ${data[item.key as keyof FuneralCostsData] ? 'text-ink line-through text-ink-4' : 'text-ink'}`}>
                {item.label}
              </span>
              <Toggle label="" checked={!!data[item.key as keyof FuneralCostsData]}
                onChange={v => upd(item.key as keyof FuneralCostsData, v as boolean)} />
            </div>
          ))}
        </div>
      </Card>

      <p className="text-xs text-ink-4 leading-relaxed px-1">
        WilsApp legt uw wensen vast als persoonlijke leidraad voor uw nabestaanden.
        Dit vervangt geen officieel financieel of juridisch advies.
      </p>
      {data.hasInsurance !== '' && (
        <ClosingGuidance title="Uw verzekeringen zijn vastgelegd">
          Controleer af en toe of uw verzekeringen nog actueel zijn en of uw naasten weten waar belangrijke documenten te vinden zijn.
        </ClosingGuidance>
      )}
    </div>
  )
}
